import { useCallback, useEffect } from 'react';

import {
  matchPath,
  To,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';

import { ROUTES } from '~app/router/routes';

const APP_HISTORY_STORAGE_KEY = 'rentify:app-history-keys';

const readHistoryKeys = () => {
  try {
    const value = sessionStorage.getItem(APP_HISTORY_STORAGE_KEY);

    if (!value) {
      return [];
    }

    const parsedValue = JSON.parse(value);

    return Array.isArray(parsedValue)
      ? parsedValue.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
};

const writeHistoryKeys = (keys: string[]) => {
  try {
    sessionStorage.setItem(APP_HISTORY_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Back navigation still works with fallback when sessionStorage is blocked.
  }
};

const matchRoute = (pathname: string, route: string) =>
  matchPath({ path: route, end: true }, pathname);

const getDefaultBackFallback = (pathname: string) => {
  if (
    matchRoute(pathname, ROUTES.TENANT_PAYMENT) ||
    matchRoute(pathname, ROUTES.TENANT_NEW_PAYMENT)
  ) {
    return ROUTES.TENANT_PAYMENTS;
  }

  if (matchRoute(pathname, ROUTES.TENANT_INVOICE)) {
    return ROUTES.TENANT_INVOICES;
  }

  if (matchRoute(pathname, ROUTES.TENANT_LEASE)) {
    return ROUTES.TENANT_LEASES;
  }

  if (matchRoute(pathname, ROUTES.TENANT_PROPERTY)) {
    return ROUTES.TENANT_PROPERTIES;
  }

  if (matchRoute(pathname, ROUTES.TENANT_REMINDERS)) {
    return ROUTES.TENANT_SCOPE;
  }

  if (pathname.startsWith(ROUTES.TENANT_SCOPE)) {
    return ROUTES.TENANT_SCOPE;
  }

  if (
    matchRoute(pathname, ROUTES.PAYMENT_RECORD) ||
    matchRoute(pathname, ROUTES.NEW_PAYMENT_RECORD) ||
    matchRoute(pathname, ROUTES.EDIT_PAYMENT_RECORD)
  ) {
    return ROUTES.PAYMENTS_RECORDS;
  }

  if (
    matchRoute(pathname, ROUTES.INVOICE) ||
    matchRoute(pathname, ROUTES.NEW_INVOICE) ||
    matchRoute(pathname, ROUTES.EDIT_INVOICE)
  ) {
    return ROUTES.INVOICES;
  }

  if (
    matchRoute(pathname, ROUTES.REMINDER) ||
    matchRoute(pathname, ROUTES.NEW_REMINDER) ||
    matchRoute(pathname, ROUTES.EDIT_REMINDER)
  ) {
    return ROUTES.REMINDERS;
  }

  if (
    matchRoute(pathname, ROUTES.LEASE) ||
    matchRoute(pathname, ROUTES.NEW_LEASE) ||
    matchRoute(pathname, ROUTES.EDIT_LEASE) ||
    matchRoute(pathname, ROUTES.LEASE_CREATED)
  ) {
    return ROUTES.LEASES;
  }

  if (
    matchRoute(pathname, ROUTES.PROPERTY) ||
    matchRoute(pathname, ROUTES.NEW_PROPERTY) ||
    matchRoute(pathname, ROUTES.EDIT_PROPERTY)
  ) {
    return ROUTES.PROPERTIES;
  }

  if (
    matchRoute(pathname, ROUTES.TENANT) ||
    matchRoute(pathname, ROUTES.NEW_TENANT) ||
    matchRoute(pathname, ROUTES.EDIT_TENANT)
  ) {
    return ROUTES.TENANTS;
  }

  if (matchRoute(pathname, ROUTES.USER)) {
    return ROUTES.DASHBOARD;
  }

  if (pathname.startsWith(ROUTES.ADMIN)) {
    return ROUTES.ADMIN_USERS;
  }

  return ROUTES.DASHBOARD;
};

export const useAppBack = (fallback?: To) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  useEffect(() => {
    const currentKey = location.key;
    const historyKeys = readHistoryKeys();

    if (currentKey === 'default') {
      writeHistoryKeys([currentKey]);
      return;
    }

    const currentIndex = historyKeys.indexOf(currentKey);

    if (navigationType === 'PUSH') {
      writeHistoryKeys(
        currentIndex >= 0
          ? historyKeys.slice(0, currentIndex + 1)
          : [...historyKeys, currentKey],
      );
      return;
    }

    if (navigationType === 'REPLACE') {
      writeHistoryKeys(
        historyKeys.length > 0
          ? [...historyKeys.slice(0, -1), currentKey]
          : [currentKey],
      );
      return;
    }

    writeHistoryKeys(
      currentIndex >= 0 ? historyKeys.slice(0, currentIndex + 1) : [currentKey],
    );
  }, [location.key, navigationType]);

  return useCallback(() => {
    const historyKeys = readHistoryKeys();

    if (historyKeys.length > 1) {
      navigate(-1);
      return;
    }

    navigate(fallback || getDefaultBackFallback(location.pathname), {
      replace: true,
    });
  }, [fallback, location.pathname, navigate]);
};
